import { Divider } from 'antd'
import {
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    ScriptableContext,
    ScriptableLineSegmentContext,
    Title,
    Tooltip,
} from 'chart.js'
import moment from 'moment'
import { FC, useEffect, useState } from 'react'
import { Line } from 'react-chartjs-2'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../../../../app/hook'
import { SVGs } from '../../../../../assets'
import { BitazzaText } from '../../../../../components'
import { MOMENT_UTIL } from '../../../../../util/formatChartlabel'
import { fetchChartPerformance } from '../../../myProfileSlice'
import Styles from './Chart.module.scss'
import { ChartSegment } from './ChartSegment'
import { PerformanceIndexModal } from './PerformanceIndexModal'
import { TimeSegment } from './TimeSegment'

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Filler,
    Legend
)

interface Props {}

export const PerformanceChart: FC<Props> = () => {
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [segmentValue, setSegmentValue] = useState<string>('ps')
    const [labels, setLabel] = useState<[]>([])
    const [chartPerformance, setChartPerformance] = useState<any>([])
    const [intervalSteps, setIntervalSteps] = useState<number>(4)
    const [isBtcClicked, setIsBtcClicked] = useState<boolean>(false)
    const [selectedTime, setSelectedTime] = useState<string>('1W')
    const dispatch = useAppDispatch()

    const options = {
        responsive: true,
        elements: {
            point: {
                radius: 0,
            },
        },
        plugins: {
            legend: {
                display: false,
            },
        },
        scales: {
            y: {
                display: true,
                position: 'right' as any,
                grid: {
                    color: 'rgba(128, 127, 123, 0.16)',
                },
            },
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    autoSkip: true,
                    maxTicksLimit: intervalSteps,
                    maxRotation: 0,
                    minRotation: 0,
                },
            },
        },

        label: {},
    }
    const selectTimestampHandler = async (time: string) => {
        let startDate = moment()
        let interval = '30m'
        switch (time) {
            case '1D':
                startDate = startDate.subtract(1, 'days')
                setIntervalSteps(4)
                break
            case '1W':
                startDate = startDate.subtract(1, 'week')
                interval = '2h'
                setIntervalSteps(4)
                break
            case '1M':
                startDate = startDate.subtract(1, 'month')
                interval = '1d'
                setIntervalSteps(4)
                break
            case '3M':
                startDate = startDate.subtract(3, 'month')
                interval = '1d'
                setIntervalSteps(3)
                break
        }

        const result = await dispatch(
            fetchChartPerformance({
                mockAccId: searchParams.get('search'),
                startDate: startDate.unix(),
                timeFormat: interval,
            })
        )

        const newChartPerformance = result?.payload?.filter(
            (res: any) => res.nav !== null
        )
        setChartPerformance(newChartPerformance)
        setLabel(MOMENT_UTIL.formatChartLabel(result.payload, time))
    }

    const onTimeStampHandler = (value: string) => {
        selectTimestampHandler(value)
        setSelectedTime(value)
        value === '1D' && setIsBtcClicked(false)
    }

    useEffect(() => {
        selectTimestampHandler('1W')

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const pnlValues = chartPerformance
        .filter((res: any) => res?.[segmentValue] != null)
        .map((res: any) => res[segmentValue])

    const positiveData = pnlValues.map((v: number) => (v >= 0 ? v : null))
    const negativeData = pnlValues.map((v: number) => (v < 0 ? v : null))

    const data = {
        labels,
        datasets: [
            {
                label: 'PNL',
                data: pnlValues,
                fill: {
                    target: 'origin',
                    above: 'rgba(16, 244, 139, 0.30)',
                    below: 'rgba(239, 65, 80, 0.30)',
                },

                borderWidth: 0.6,
                tension: 0.08,
                pointRadius: 0,
                pointBackgroundColor: (ctx: any) =>
                    ctx?.parsed?.y >= 0 ? '#10F48B' : '#EF4150',
                segment: {
                    borderColor: '#E0E0E0',
                },
            },
            {
                fill: true,
                data:
                    isBtcClicked &&
                    chartPerformance?.map((res: any) => {
                        return res?.bi
                    }),
                borderColor: '#F7931A',
                borderWidth: 1,
                backgroundColor: (context: ScriptableContext<'line'>) => {
                    const ctx = context?.chart?.ctx
                    const gradient = ctx?.createLinearGradient(0, 0, 0, 400)
                    gradient.addColorStop(0, 'rgba(247, 147, 26, 0.4)')
                    gradient.addColorStop(1, 'rgba(247, 147, 26, 0.00)')
                    return gradient
                },
            },
        ],
    }

    const btcHandler = () => {
        setIsBtcClicked(!isBtcClicked)
    }

    return (
        <>
            <PerformanceIndexModal
                isModalOpen={isModalOpen}
                onCLoseModal={() => setIsModalOpen(false)}
            />
            <div className={Styles.container}>
                <div className={Styles.segmentHeader}>
                    <ChartSegment
                        onTableSegment={(value) => setSegmentValue(value)}
                        segmentValue={segmentValue}
                    />
                    {segmentValue === 'nav' ? (
                        <div className={Styles.performanceIndexContainer}>
                            <div className={Styles.textWrapper}>
                                <BitazzaText
                                    format="t14"
                                    content={t('txt_performance')}
                                    color="#86A1A1"
                                    display="block"
                                    lineHeight={14}
                                />
                                <BitazzaText
                                    format="t14"
                                    content={t('text_index')}
                                    color="#86A1A1"
                                    lineHeight={14}
                                />
                            </div>
                            <img
                                src={SVGs.info}
                                alt="info"
                                width={16}
                                onClick={() => setIsModalOpen(true)}
                            />
                        </div>
                    ) : null}
                </div>

                <Divider className={Styles.segmentDivider} />
                <Line options={options} data={data} />
            </div>
            <Divider className={Styles.segmentDivider} />

            {selectedTime !== '1D' ? (
                <div className={Styles.chartText}>
                    <div className={Styles.compare}>
                        {isBtcClicked ? (
                            <img
                                src={SVGs.fillCheckbox}
                                onClick={btcHandler}
                                width={18}
                                alt="fillCheckbox"
                            />
                        ) : (
                            <img
                                src={SVGs.checkbox}
                                onClick={btcHandler}
                                width={18}
                                alt="checkbox"
                            />
                        )}
                        <BitazzaText
                            format="t14"
                            content="Compare against BTC"
                            color="#FFFF"
                            display="block"
                            lineHeight={14}
                        />
                    </div>

                    <div className={Styles.performanceBtcLine}>
                        <>
                            <div className={Styles.performance} />
                            <BitazzaText
                                format="t14"
                                content="Your Performance"
                                color="#86A1A1"
                                lineHeight={14}
                            />
                        </>
                        <>
                            <div className={Styles.btc} />
                            <BitazzaText
                                format="t14"
                                content="BTC"
                                color="#86A1A1"
                                lineHeight={14}
                            />
                        </>
                    </div>
                </div>
            ) : null}

            <TimeSegment
                onTimeHandler={onTimeStampHandler}
                selected={selectedTime}
            />
        </>
    )
}
